import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCheckIcon, AlertCircle, Search, Filter, Bell, Send, X, Clock, AlertTriangle, XCircle, UserX, RefreshCcw } from "lucide-react";

import "./Notificacoes.css";
import NotificationCard from "../../../components/NotificationCard/NotificationCard";
import { CriarNotificacaoModal } from "../../../components/CriarNotificacaoModal/CriarNotificacaoModal";
import FilterOptions from "../../../components/FilterOption/FilterOptions";
import { EditNotification, NotificationTemplateShape } from "../../../components/EditNotification/EditNotification";

import api from "@/utils/connector/ApiConnector";
import { useUser } from "@/utils/userContext/UserContext";
import NotificationList, { NotificationTemplate } from "./NotificationList";
import { useGlobalModal } from "../../../components/GlobalModalProvider/GlobalModalProvider";

// Customer type for manual send with subscription status
type SubscriptionStatus = 'active' | 'expiring_soon' | 'expired' | 'no_subscription';

type Customer = {
  id: string;
  name: string;
  email: string;
  subscriptionStatus?: SubscriptionStatus;
  subscriptionEndDate?: string | null;
  daysUntilExpiration?: number | null;
  product?: { id: string; name: string } | null;
};

// Manual Send Modal Component
function ManualSendModal({
  template,
  onClose,
  onSent,
}: {
  template: NotificationTemplate;
  onClose: () => void;
  onSent: () => void;
}) {
  const userContext = useUser();
  const token = userContext?.userData?.auth;
  const modal = useGlobalModal();

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<SubscriptionStatus | 'all'>('all');

  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, []);

  useEffect(() => {
    async function loadCustomers() {
      if (!token) return;
      setLoading(true);
      try {
        // Use the new endpoint that includes subscription status
        const resp = await api.get("/customer/with-subscription-status", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const raw = (resp as any)?.data ?? resp;
        let list: Customer[] = [];
        if (Array.isArray(raw)) list = raw;
        else if (Array.isArray(raw.items)) list = raw.items;
        else if (Array.isArray(raw.data)) list = raw.data;
        setCustomers(list);
      } catch (err) {
        console.error("Erro ao buscar clientes:", err);
        // Fallback to regular endpoint if new one fails
        try {
          const resp = await api.get("/customer", {
            headers: { Authorization: `Bearer ${token}` },
          });
          const raw = (resp as any)?.data ?? resp;
          let list: Customer[] = [];
          if (Array.isArray(raw)) list = raw;
          else if (Array.isArray(raw.items)) list = raw.items;
          else if (Array.isArray(raw.data)) list = raw.data;
          setCustomers(list);
        } catch (fallbackErr) {
          console.error("Fallback também falhou:", fallbackErr);
        }
      } finally {
        setLoading(false);
      }
    }
    loadCustomers();
  }, [token]);

  const filteredCustomers = customers.filter((c) => {
    const matchesSearch =
      c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === 'all' || c.subscriptionStatus === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Count customers by status
  const statusCounts = {
    all: customers.length,
    active: customers.filter((c) => c.subscriptionStatus === 'active').length,
    expiring_soon: customers.filter((c) => c.subscriptionStatus === 'expiring_soon').length,
    expired: customers.filter((c) => c.subscriptionStatus === 'expired').length,
    no_subscription: customers.filter((c) => c.subscriptionStatus === 'no_subscription').length,
  };

  // Select all filtered by current status
  const selectAllByStatus = (status: SubscriptionStatus | 'all') => {
    const toSelect = customers
      .filter((c) => status === 'all' || c.subscriptionStatus === status)
      .map((c) => c.id);
    setSelectedIds((prev) => [...new Set([...prev, ...toSelect])]);
  };

  // Deselect all filtered by current status
  const deselectAllByStatus = (status: SubscriptionStatus | 'all') => {
    const toDeselect = new Set(
      customers
        .filter((c) => status === 'all' || c.subscriptionStatus === status)
        .map((c) => c.id)
    );
    setSelectedIds((prev) => prev.filter((id) => !toDeselect.has(id)));
  };

  // Helper to get status badge info
  const getStatusBadge = (status?: SubscriptionStatus) => {
    switch (status) {
      case 'active':
        return { label: 'Ativo', color: '#22c55e', bgColor: '#dcfce7', Icon: CheckCheckIcon };
      case 'expiring_soon':
        return { label: 'Expirando', color: '#f59e0b', bgColor: '#fef3c7', Icon: AlertTriangle };
      case 'expired':
        return { label: 'Expirado', color: '#ef4444', bgColor: '#fee2e2', Icon: XCircle };
      case 'no_subscription':
        return { label: 'Sem assinatura', color: '#6b7280', bgColor: '#f3f4f6', Icon: UserX };
      default:
        return null;
    }
  };

  const toggleCustomer = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    setSelectedIds(filteredCustomers.map((c) => c.id));
  };

  const deselectAll = () => {
    setSelectedIds([]);
  };

  const handleSend = async () => {
    if (selectedIds.length === 0) {
      modal?.show({
        type: "warning",
        title: "Nenhum cliente selecionado",
        message: "Selecione pelo menos um cliente para enviar.",
        autoClose: 3000,
      });
      return;
    }

    setSending(true);
    try {
      const resp = await api.post(
        `/notifications/templates/${template.id}/send`,
        { customerIds: selectedIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const result = (resp as any)?.data ?? resp;

      modal?.show({
        type: "success",
        title: "Envio concluído",
        message: `Enviados: ${result.sent}, Falhas: ${result.failed}`,
        autoClose: 5000,
      });

      onSent();
      onClose();
    } catch (err: any) {
      console.error("Erro ao enviar:", err);
      modal?.show({
        type: "error",
        title: "Erro",
        message: err?.response?.data?.message ?? "Erro ao enviar notificações",
        autoClose: 5000,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content manual-send-modal">
        <button className="modal-close" onClick={onClose}>
          <X size={20} />
        </button>

        <h2 className="modal-title">Enviar Notificação</h2>
        <p className="modal-subtitle">
          Enviar "{template.title}" para clientes selecionados
        </p>

        <div className="customer-search">
          <Search size={16} />
          <input
            type="text"
            placeholder="Buscar clientes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Status Filter Tabs */}
        <div className="status-filter-tabs">
          <button
            type="button"
            className={`status-tab ${statusFilter === 'all' ? 'active' : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            Todos ({statusCounts.all})
          </button>
          <button
            type="button"
            className={`status-tab status-active ${statusFilter === 'active' ? 'active' : ''}`}
            onClick={() => setStatusFilter('active')}
          >
            <CheckCheckIcon size={12} />
            Ativos ({statusCounts.active})
          </button>
          <button
            type="button"
            className={`status-tab status-expiring ${statusFilter === 'expiring_soon' ? 'active' : ''}`}
            onClick={() => setStatusFilter('expiring_soon')}
          >
            <AlertTriangle size={12} />
            Expirando ({statusCounts.expiring_soon})
          </button>
          <button
            type="button"
            className={`status-tab status-expired ${statusFilter === 'expired' ? 'active' : ''}`}
            onClick={() => setStatusFilter('expired')}
          >
            <XCircle size={12} />
            Expirados ({statusCounts.expired})
          </button>
          <button
            type="button"
            className={`status-tab status-none ${statusFilter === 'no_subscription' ? 'active' : ''}`}
            onClick={() => setStatusFilter('no_subscription')}
          >
            <UserX size={12} />
            Sem assin. ({statusCounts.no_subscription})
          </button>
        </div>

        <div className="customer-actions">
          <button type="button" className="btn-link" onClick={() => selectAllByStatus(statusFilter)}>
            Selecionar {statusFilter === 'all' ? 'todos' : 'filtrados'} ({filteredCustomers.length})
          </button>
          <button type="button" className="btn-link" onClick={() => deselectAllByStatus(statusFilter)}>
            Limpar {statusFilter === 'all' ? 'todos' : 'filtrados'}
          </button>
          <button type="button" className="btn-link" onClick={deselectAll}>
            Limpar tudo
          </button>
          <span className="selected-count">{selectedIds.length} selecionados</span>
        </div>

        {loading ? (
          <div className="customer-loading">Carregando clientes...</div>
        ) : (
          <div className="customer-list">
            {filteredCustomers.length === 0 ? (
              <div className="customer-empty">Nenhum cliente encontrado.</div>
            ) : (
              filteredCustomers.map((c) => {
                const badge = getStatusBadge(c.subscriptionStatus);
                return (
                  <label key={c.id} className="customer-item">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(c.id)}
                      onChange={() => toggleCustomer(c.id)}
                    />
                    <div className="customer-info">
                      <div className="customer-name-row">
                        <span className="customer-name">{c.name}</span>
                        {badge && (
                          <span
                            className="subscription-badge"
                            style={{
                              backgroundColor: badge.bgColor,
                              color: badge.color,
                            }}
                            title={
                              c.daysUntilExpiration !== null && c.daysUntilExpiration !== undefined
                                ? c.daysUntilExpiration >= 0
                                  ? `Expira em ${c.daysUntilExpiration} dia(s)`
                                  : `Expirou há ${Math.abs(c.daysUntilExpiration)} dia(s)`
                                : undefined
                            }
                          >
                            <badge.Icon size={12} />
                            {badge.label}
                          </span>
                        )}
                      </div>
                      <span className="customer-email">{c.email}</span>
                      {c.subscriptionEndDate && (
                        <span className="customer-subscription-date">
                          <Clock size={12} />
                          {c.daysUntilExpiration !== null && c.daysUntilExpiration !== undefined
                            ? c.daysUntilExpiration >= 0
                              ? `Expira em ${c.daysUntilExpiration} dia(s) (${c.subscriptionEndDate})`
                              : `Expirou há ${Math.abs(c.daysUntilExpiration)} dia(s) (${c.subscriptionEndDate})`
                            : c.subscriptionEndDate}
                        </span>
                      )}
                    </div>
                  </label>
                );
              })
            )}
          </div>
        )}

        <div className="modal-actions">
          <button
            type="button"
            className="btn-primary"
            onClick={handleSend}
            disabled={sending || selectedIds.length === 0}
          >
            <Send size={16} />
            {sending ? "Enviando..." : `Enviar para ${selectedIds.length} cliente(s)`}
          </button>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Notificacoes() {
  const [filterButtonOpened, setFilterButtonOpened] = useState(false);
  const [createNotificationClicked, setCreateNotificationClicked] = useState(false);

  const userContext = useUser();
  const token = userContext?.userData?.auth;
  const queryClient = useQueryClient();

  // Edit modal state
  const [editingTemplate, setEditingTemplate] = useState<NotificationTemplateShape | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);

  // Manual send modal state
  const [sendingTemplate, setSendingTemplate] = useState<NotificationTemplate | null>(null);
  const [sendModalOpen, setSendModalOpen] = useState(false);

  // React Query for notification counts
  const { data: notifCounts = { total: 0, active: 0, inactive: 0 }, isLoading: notifLoading, error: notifErrorObj, refetch: refetchNotifications } = useQuery({
    queryKey: ['notificationCounts'],
    queryFn: async () => {
      const resp = await api.get("/notifications/templates", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const raw = (resp && (resp as any).data) ? (resp as any).data : resp;

      // extrai array (vários formatos possíveis)
      let list: any[] = [];
      if (Array.isArray(raw)) list = raw;
      else if (Array.isArray(raw.items)) list = raw.items;
      else if (Array.isArray(raw.data)) list = raw.data;
      else {
        const maybe = Object.values(raw).find((v) => Array.isArray(v));
        if (Array.isArray(maybe)) list = maybe as any[];
      }

      // normaliza: extrai .props quando aplicável
      const normalized = list.map((t: any) => {
        const maybeProps = t?.props ?? t;
        const id = maybeProps?.id ?? maybeProps?._id ?? t?.id ?? t?._id ?? undefined;
        return { ...(maybeProps || {}), id };
      });

      const total = normalized.length;
      const active = normalized.filter((x: any) => {
        return !!(x.active ?? x.props?.active);
      }).length;
      const inactive = total - active;

      return { total, active, inactive };
    },
    enabled: !!token,
  });

  const notifError = notifErrorObj ? ((notifErrorObj as any)?.response?.data?.message ?? (notifErrorObj as Error)?.message ?? "Erro ao buscar notificações") : null;

  const handleEditTemplate = (tpl: NotificationTemplateShape) => {
    setEditingTemplate(tpl);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingTemplate(null);
  };

  const handleManualSend = (tpl: NotificationTemplate) => {
    setSendingTemplate(tpl);
    setSendModalOpen(true);
  };

  const handleCloseSendModal = () => {
    setSendModalOpen(false);
    setSendingTemplate(null);
  };

  const [isRefreshing, setIsRefreshing] = useState(false);

  const refreshList = () => {
    queryClient.invalidateQueries({ queryKey: ['notificationCounts'] });
    queryClient.invalidateQueries({ queryKey: ['notificationTemplates'] });
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await queryClient.invalidateQueries({ queryKey: ['notificationCounts'] });
    await queryClient.invalidateQueries({ queryKey: ['notificationTemplates'] });
    setIsRefreshing(false);
  };

  return (
    <>
      <div className="notificacoes-header">
        <div>
          <h1 className="gradient-heading">Notificações</h1>
          <p className="h2 dashboard-page-subtitle">Crie, edite e acompanhe as notificações do sistema</p>
        </div>
        <div className="notificacoes-header-actions">
          <button
            className={`refresh-button ${isRefreshing ? 'spinning' : ''}`}
            onClick={handleRefresh}
            disabled={isRefreshing}
            title="Atualizar dados"
          >
            <RefreshCcw size={16} />
            Atualizar
          </button>
          <button
            className="edit-button"
            onClick={() => setCreateNotificationClicked((prev) => !prev)}
          >
            <CheckCheckIcon size={16} />
            Criar Nova notificação
          </button>
        </div>
      </div>

      {createNotificationClicked && (
        <CriarNotificacaoModal
          onClose={() => {
            setCreateNotificationClicked(false);
            refreshList();
          }}
          onCreated={refreshList}
        />
      )}

      <div className="notificacoes-container">
        {notifLoading ? (
          <div style={{ display: "flex", gap: 12 }}>
            <div>Carregando...</div>
          </div>
        ) : notifError ? (
          <div style={{ color: "red" }}>Erro: {notifError}</div>
        ) : (
          <>
            <NotificationCard
              title="Total"
              value={notifCounts.total}
              Icon={Bell}
              bgColor="#F3F4F6"
              textColor="#000000"
              iconColor="#7e22ce"
            />

            <NotificationCard
              title="Ativos"
              value={notifCounts.active}
              Icon={CheckCheckIcon}
              bgColor="#F3F4F6"
              textColor="#000000"
              iconColor="#22ce39"
            />

            <NotificationCard
              title="Inativos"
              value={notifCounts.inactive}
              Icon={AlertCircle}
              bgColor="#F3F4F6"
              textColor="#000000"
              iconColor="#374151"
            />
          </>
        )}
      </div>

      <div className="container-filter-input">
        <div className="input-container">
          <Search style={{ display: "flex", position: "relative", left: "10px" }} size={16} />
          <input type="text" name="" id="" placeholder="Buscar Notificações" />
        </div>

        <button
          className="filter-button"
          onClick={() => setFilterButtonOpened((prev) => !prev)}
        >
          <Filter size={16} /> Filtros
        </button>

        <div className="filter-overlay">{filterButtonOpened && <FilterOptions />}</div>
      </div>

      <div className="configuration-notif-container-h1">
        <h1>Configurações de Notificações</h1>
      </div>

      <div className="configuration-notif-container">
        <NotificationList
          fetchLogs={true}
          onEditTemplate={handleEditTemplate}
          onManualSend={handleManualSend}
        />
      </div>

      {editModalOpen && editingTemplate && (
        <EditNotification
          template={editingTemplate}
          onClose={handleCloseEditModal}
          onSaved={() => {
            handleCloseEditModal();
            refreshList();
          }}
        />
      )}

      {sendModalOpen && sendingTemplate && (
        <ManualSendModal
          template={sendingTemplate}
          onClose={handleCloseSendModal}
          onSent={refreshList}
        />
      )}
    </>
  );
}
